import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  Wrench,
  Plus,
  Search,
  Edit,
  Trash2,
  Calendar,
  DollarSign,
  Car,
  Gauge,
  X,
  Filter
} from 'lucide-react';

const tipoColors = {
  preventiva: 'bg-green-100 text-green-800',
  corretiva: 'bg-red-100 text-red-800',
  revisao: 'bg-blue-100 text-blue-800',
  troca_oleo: 'bg-yellow-100 text-yellow-800',
  outro: 'bg-gray-100 text-gray-800'
};

const tipoLabels = {
  preventiva: 'Preventiva',
  corretiva: 'Corretiva',
  revisao: 'Revisão',
  troca_oleo: 'Troca de Óleo',
  outro: 'Outro'
};

const mesesLabels = [
  { value: '1',  label: 'Janeiro' },
  { value: '2',  label: 'Fevereiro' },
  { value: '3',  label: 'Março' },
  { value: '4',  label: 'Abril' },
  { value: '5',  label: 'Maio' },
  { value: '6',  label: 'Junho' },
  { value: '7',  label: 'Julho' },
  { value: '8',  label: 'Agosto' },
  { value: '9',  label: 'Setembro' },
  { value: '10', label: 'Outubro' },
  { value: '11', label: 'Novembro' },
  { value: '12', label: 'Dezembro' },
];

export default function MaintenancesPage() {
  const { API } = useAuth();
  const [maintenances, setMaintenances] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [vehicleFilter, setVehicleFilter] = useState('todos');
  const [tipoFilter, setTipoFilter] = useState('todos');

  // Date filters
  const currentYear = new Date().getFullYear();
  const [mesFilter, setMesFilter] = useState('');
  const [anoFilter, setAnoFilter] = useState(currentYear.toString());
  const [dataInicio, setDataInicio] = useState('');
  const [dataFim, setDataFim] = useState('');

  // Check if any date filter is active
  const hasDateFilter = mesFilter || dataInicio || dataFim;
  
  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedMaintenance, setSelectedMaintenance] = useState(null);
  const [formMode, setFormMode] = useState('create');
  
  // Form state
  const [formData, setFormData] = useState({
    vehicle_id: '',
    data_manutencao: new Date().toISOString().split('T')[0],
    tipo: 'preventiva',
    descricao: '',
    valor: 0,
    km_veiculo: 0,
    oficina: '',
    observacoes: ''
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    loadMaintenances();
    loadVehicles();
  }, [vehicleFilter, tipoFilter, mesFilter, anoFilter, dataInicio, dataFim]);

  const loadMaintenances = async () => {
    try {
      const params = new URLSearchParams();
      if (vehicleFilter !== 'todos') params.append('vehicle_id', vehicleFilter);
      if (tipoFilter !== 'todos') params.append('tipo', tipoFilter);
      // A busca por texto é feita no front (filteredMaintenances) para ter a mesma
      // regra em tudo; enviar 'search' aqui aplicava um filtro extra mais frouxo no servidor.

      // Date filtering: month takes priority; custom range is a fallback
      if (mesFilter) {
        const month = parseInt(mesFilter);
        const year = parseInt(anoFilter);
        const start = new Date(year, month - 1, 1);
        const end   = new Date(year, month, 0); // last day of the month
        params.append('data_inicio', start.toISOString().split('T')[0]);
        params.append('data_fim',    end.toISOString().split('T')[0]);
      } else {
        if (dataInicio) params.append('data_inicio', dataInicio);
        if (dataFim)    params.append('data_fim', dataFim);
      }
      
      const response = await axios.get(`${API}/maintenances?${params}`, { withCredentials: true });
      setMaintenances(response.data);
    } catch (error) {
      toast.error('Erro ao carregar manutenções');
    } finally {
      setLoading(false);
    }
  };

  const loadVehicles = async () => {
    try {
      const response = await axios.get(`${API}/vehicles`, { withCredentials: true });
      setVehicles(response.data);
    } catch (error) {
      console.error('Error loading vehicles:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.vehicle_id) {
      toast.error('Selecione um veículo');
      return;
    }
    
    if (!formData.descricao.trim()) {
      toast.error('Informe a descrição da manutenção');
      return;
    }
    
    setSubmitting(true);
    
    try {
      if (formMode === 'create') {
        await axios.post(`${API}/maintenances`, formData, { withCredentials: true });
        toast.success('Manutenção registrada com sucesso!');
      } else {
        await axios.put(`${API}/maintenances/${selectedMaintenance.maintenance_id}`, formData, { withCredentials: true });
        toast.success('Manutenção atualizada com sucesso!');
      }
      setShowModal(false);
      resetForm();
      loadMaintenances();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar manutenção');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/maintenances/${selectedMaintenance.maintenance_id}`, { withCredentials: true });
      toast.success('Manutenção excluída com sucesso!');
      setShowDeleteDialog(false);
      loadMaintenances();
    } catch (error) {
      toast.error('Erro ao excluir manutenção');
    }
  };

  const resetForm = () => {
    setFormData({
      vehicle_id: '',
      data_manutencao: new Date().toISOString().split('T')[0],
      tipo: 'preventiva',
      descricao: '',
      valor: 0,
      km_veiculo: 0,
      oficina: '',
      observacoes: ''
    });
    setSelectedMaintenance(null);
  };

  const openCreateModal = () => {
    setFormMode('create');
    resetForm();
    setShowModal(true);
  };

  const openEditModal = (maintenance) => {
    setFormMode('edit');
    setSelectedMaintenance(maintenance);
    setFormData({
      vehicle_id: maintenance.vehicle_id,
      data_manutencao: maintenance.data_manutencao,
      tipo: maintenance.tipo,
      descricao: maintenance.descricao,
      valor: maintenance.valor,
      km_veiculo: maintenance.km_veiculo,
      oficina: maintenance.oficina,
      observacoes: maintenance.observacoes
    });
    setShowModal(true);
  };

  const openDeleteDialog = (maintenance) => {
    setSelectedMaintenance(maintenance);
    setShowDeleteDialog(true);
  };

  // When selecting a month: clear custom date range
  const handleMesChange = (value) => {
    setMesFilter(value === 'todos' ? '' : value);
    if (value && value !== 'todos') {
      setDataInicio('');
      setDataFim('');
    }
  };

  // When typing custom dates: clear month filter
  const handleDataInicioChange = (value) => {
    setDataInicio(value);
    setMesFilter('');
  };

  const handleDataFimChange = (value) => {
    setDataFim(value);
    setMesFilter('');
  };

  // Reset all date filters
  const clearDateFilters = () => {
    setMesFilter('');
    setAnoFilter(currentYear.toString());
    setDataInicio('');
    setDataFim('');
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr + 'T00:00:00').toLocaleDateString('pt-BR');
  };

  // Veículos vendidos ficam fora da lista, exceto quando filtrados/buscados de propósito
  const vehicleById = useMemo(
    () => Object.fromEntries(vehicles.map((v) => [v.vehicle_id, v])),
    [vehicles]
  );
  const activeVehicles = useMemo(() => vehicles.filter((v) => v.status !== 'vendido'), [vehicles]);
  const soldVehicles = useMemo(() => vehicles.filter((v) => v.status === 'vendido'), [vehicles]);

  // Minúsculas, sem acento e sem espaços repetidos
  const norm = (text) =>
    (text || '').toString().normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase().replace(/\s+/g, ' ').trim();
  const escapeRegex = (text) => text.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Termo casa no INÍCIO de uma palavra: "9A" acha "9A FACTOR", mas não "19A FACTOR"
  const matchesWordStart = (text, term) =>
    new RegExp(`(^|[^a-z0-9])${escapeRegex(term)}`).test(norm(text));

  // Identificação do veículo: ID, placa e marca/modelo
  const matchesVehicleTerm = (m, term) => {
    const v = vehicleById[m.vehicle_id];
    return (
      matchesWordStart(m.vehicle_id, term) ||
      matchesWordStart(m.veiculo_info, term) ||
      (v && (matchesWordStart(v.placa, term) || matchesWordStart(`${v.marca} ${v.modelo}`, term)))
    );
  };

  const filteredMaintenances = useMemo(() => {
    const term = norm(search);
    return maintenances.filter((m) => {
      const isSold = vehicleById[m.vehicle_id]?.status === 'vendido';
      const vehicleSelected = vehicleFilter !== 'todos';
      const vehicleMatch = term ? matchesVehicleTerm(m, term) : false;

      // Vendido só aparece se foi escolhido no filtro ou encontrado pela busca
      if (isSold && !vehicleSelected && !vehicleMatch) return false;
      if (!term) return true;

      return (
        vehicleMatch ||
        norm(m.descricao).includes(term) ||
        norm(m.oficina).includes(term) ||
        norm(tipoLabels[m.tipo] || m.tipo).includes(term)
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [maintenances, search, vehicleFilter, vehicleById]);

  // Total gasto calculated locally from filtered results
  const totalGasto = useMemo(
    () => filteredMaintenances.reduce((sum, m) => sum + (m.valor || 0), 0),
    [filteredMaintenances]
  );

  return (
    <div className="space-y-6" data-testid="maintenances-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
            <Wrench className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h1 className="text-2xl font-bold font-[Manrope] text-gray-900">Manutenções</h1>
            <p className="text-gray-500 text-sm">Gerencie as manutenções dos veículos</p>
          </div>
        </div>
        <Button onClick={openCreateModal} className="bg-orange-500 hover:bg-orange-600" data-testid="add-maintenance-button">
          <Plus className="w-4 h-4 mr-2" />
          Nova Manutenção
        </Button>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4 space-y-3">
          {/* Row 1: search + vehicle + tipo */}
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Buscar por ID, placa, veículo, descrição ou oficina..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10"
                data-testid="search-maintenances"
              />
            </div>
            <Select value={vehicleFilter} onValueChange={setVehicleFilter}>
              <SelectTrigger className="w-[200px]" data-testid="vehicle-filter">
                <SelectValue placeholder="Todos os veículos" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos os veículos (sem vendidos)</SelectItem>
                {activeVehicles.map((v) => (
                  <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                    {v.vehicle_id} - {v.placa}
                  </SelectItem>
                ))}
                {soldVehicles.length > 0 && (
                  <>
                    <SelectSeparator />
                    <SelectGroup>
                      <SelectLabel>Vendidos</SelectLabel>
                      {soldVehicles.map((v) => (
                        <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                          {v.vehicle_id} - {v.placa} (vendido)
                        </SelectItem>
                      ))}
                    </SelectGroup>
                  </>
                )}
              </SelectContent>
            </Select>
            <Select value={tipoFilter} onValueChange={setTipoFilter}>
              <SelectTrigger className="w-[150px]" data-testid="tipo-filter">
                <SelectValue placeholder="Tipo" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="todos">Todos</SelectItem>
                <SelectItem value="preventiva">Preventiva</SelectItem>
                <SelectItem value="corretiva">Corretiva</SelectItem>
                <SelectItem value="revisao">Revisão</SelectItem>
                <SelectItem value="troca_oleo">Troca de Óleo</SelectItem>
                <SelectItem value="outro">Outro</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Row 2: date filters */}
          <div className="flex flex-col md:flex-row items-start md:items-center gap-3 pt-1 border-t border-gray-100">
            <div className="flex items-center gap-1 text-sm text-gray-500 shrink-0">
              <Filter className="w-3.5 h-3.5" />
              <span>Período:</span>
            </div>

            {/* Month + Year */}
            <div className="flex items-center gap-2">
              <Select value={mesFilter || 'todos'} onValueChange={handleMesChange}>
                <SelectTrigger className="w-[140px] h-9 text-sm" data-testid="mes-filter">
                  <SelectValue placeholder="Todos os meses" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="todos">Todos os meses</SelectItem>
                  {mesesLabels.map(m => (
                    <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {mesFilter && (
                <Select value={anoFilter} onValueChange={setAnoFilter}>
                  <SelectTrigger className="w-[100px] h-9 text-sm" data-testid="ano-filter">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {[currentYear - 2, currentYear - 1, currentYear, currentYear + 1].map(y => (
                      <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            {/* OR separator */}
            <span className="text-xs text-gray-400 hidden md:block">ou</span>

            {/* Custom date range */}
            <div className="flex items-center gap-2">
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500 shrink-0">De</span>
                <Input
                  type="date"
                  value={dataInicio}
                  onChange={(e) => handleDataInicioChange(e.target.value)}
                  className="h-9 text-sm w-[150px]"
                  data-testid="data-inicio-filter"
                />
              </div>
              <div className="flex items-center gap-1.5">
                <span className="text-xs text-gray-500 shrink-0">até</span>
                <Input
                  type="date"
                  value={dataFim}
                  onChange={(e) => handleDataFimChange(e.target.value)}
                  className="h-9 text-sm w-[150px]"
                  data-testid="data-fim-filter"
                />
              </div>
            </div>

            {/* Clear date filters button */}
            {hasDateFilter && (
              <Button
                variant="ghost"
                size="sm"
                onClick={clearDateFilters}
                className="h-9 text-xs text-gray-500 hover:text-gray-700 gap-1 shrink-0"
                data-testid="clear-date-filters"
              >
                <X className="w-3.5 h-3.5" />
                Limpar datas
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Stats Summary */}
      <div className="flex items-center justify-between text-sm">
        <span className="text-gray-600">
          {filteredMaintenances.length} {filteredMaintenances.length === 1 ? 'manutenção encontrada' : 'manutenções encontradas'}
        </span>
        <div className="text-right">
          <span className="text-gray-500">Total gasto</span>
          <p className="text-xl font-bold text-orange-600" data-testid="total-gasto">{formatCurrency(totalGasto)}</p>
        </div>
      </div>

      {/* Maintenance Cards */}
      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-600"></div>
        </div>
      ) : filteredMaintenances.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center h-64 text-gray-500">
            <Wrench className="w-12 h-12 mb-2 opacity-50" />
            <p>Nenhuma manutenção encontrada</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {filteredMaintenances.map((maintenance) => (
            <Card 
              key={maintenance.maintenance_id} 
              className="overflow-hidden hover:shadow-md transition-shadow"
              data-testid={`maintenance-card-${maintenance.maintenance_id}`}
            >
              <CardContent className="p-0">
                <div className="flex flex-col md:flex-row">
                  {/* Left accent bar */}
                  <div className="w-full md:w-1 h-2 md:h-auto bg-orange-500"></div>
                  
                  {/* Main content */}
                  <div className="flex-1 p-4">
                    {/* Header */}
                    <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-2 mb-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-orange-100 rounded-lg flex items-center justify-center">
                          <Wrench className="w-4 h-4 text-orange-600" />
                        </div>
                        <div>
                          <h3 className="font-bold text-gray-900">
                            {maintenance.vehicle_id ? `${maintenance.vehicle_id} - ` : ''}{maintenance.veiculo_info}
                          </h3>
                          <div className="flex items-center gap-2 mt-1">
                            <Badge className={tipoColors[maintenance.tipo] || tipoColors.outro}>
                              {tipoLabels[maintenance.tipo] || maintenance.tipo}
                            </Badge>
                            <span className="text-sm text-gray-500 flex items-center gap-1">
                              <Calendar className="w-3 h-3" />
                              {formatDate(maintenance.data_manutencao)}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      {/* Actions */}
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => openEditModal(maintenance)}
                          data-testid={`edit-maintenance-${maintenance.maintenance_id}`}
                        >
                          <Edit className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteDialog(maintenance)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          data-testid={`delete-maintenance-${maintenance.maintenance_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    
                    {/* Description */}
                    <div className="mb-3">
                      <p className="text-gray-700 text-sm leading-relaxed whitespace-pre-wrap">
                        {maintenance.descricao}
                      </p>
                    </div>
                    
                    {/* Footer info */}
                    <div className="flex flex-wrap items-center gap-4 pt-3 border-t border-gray-100">
                      <div className="flex items-center gap-1 text-sm">
                        <DollarSign className="w-4 h-4 text-green-600" />
                        <span className="font-semibold text-green-600">{formatCurrency(maintenance.valor)}</span>
                      </div>
                      {maintenance.km_veiculo > 0 && (
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Gauge className="w-4 h-4" />
                          <span>{maintenance.km_veiculo.toLocaleString('pt-BR')} km</span>
                        </div>
                      )}
                      {maintenance.oficina && (
                        <div className="flex items-center gap-1 text-sm text-gray-500">
                          <Car className="w-4 h-4" />
                          <span>{maintenance.oficina}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create/Edit Modal */}
      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {formMode === 'create' ? 'Nova Manutenção' : 'Editar Manutenção'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="vehicle_id">Veículo *</Label>
              <Select 
                value={formData.vehicle_id} 
                onValueChange={(value) => setFormData({ ...formData, vehicle_id: value })}
                disabled={formMode === 'edit'}
              >
                <SelectTrigger data-testid="form-vehicle-select">
                  <SelectValue placeholder="Selecione um veículo" />
                </SelectTrigger>
                <SelectContent>
                  {/* Nova manutenção: só veículos não vendidos. Na edição mantém o veículo atual. */}
                  {vehicles
                    .filter((v) => v.status !== 'vendido' || v.vehicle_id === formData.vehicle_id)
                    .map((v) => (
                      <SelectItem key={v.vehicle_id} value={v.vehicle_id}>
                        {v.vehicle_id} - {v.marca} {v.modelo} - {v.placa}
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="data_manutencao">Data da Manutenção *</Label>
                <Input
                  id="data_manutencao"
                  type="date"
                  value={formData.data_manutencao}
                  onChange={(e) => setFormData({ ...formData, data_manutencao: e.target.value })}
                  data-testid="form-data-manutencao"
                />
              </div>
              <div>
                <Label htmlFor="tipo">Tipo de Manutenção *</Label>
                <Select 
                  value={formData.tipo} 
                  onValueChange={(value) => setFormData({ ...formData, tipo: value })}
                >
                  <SelectTrigger data-testid="form-tipo-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="preventiva">Preventiva</SelectItem>
                    <SelectItem value="corretiva">Corretiva</SelectItem>
                    <SelectItem value="revisao">Revisão</SelectItem>
                    <SelectItem value="troca_oleo">Troca de Óleo</SelectItem>
                    <SelectItem value="outro">Outro</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="valor">Valor Gasto (R$) *</Label>
                <Input
                  id="valor"
                  type="number"
                  step="0.01"
                  min="0"
                  value={formData.valor}
                  onChange={(e) => setFormData({ ...formData, valor: parseFloat(e.target.value) || 0 })}
                  data-testid="form-valor"
                />
              </div>
              <div>
                <Label htmlFor="km_veiculo">Quilometragem</Label>
                <Input
                  id="km_veiculo"
                  type="number"
                  min="0"
                  placeholder="Ex: 50000"
                  value={formData.km_veiculo || ''}
                  onChange={(e) => setFormData({ ...formData, km_veiculo: parseFloat(e.target.value) || 0 })}
                  data-testid="form-km"
                />
              </div>
            </div>
            
            <div>
              <Label htmlFor="oficina">Oficina/Prestador</Label>
              <Input
                id="oficina"
                placeholder="Nome da oficina ou prestador de serviço"
                value={formData.oficina}
                onChange={(e) => setFormData({ ...formData, oficina: e.target.value })}
                data-testid="form-oficina"
              />
            </div>
            
            <div>
              <Label htmlFor="descricao">Descrição *</Label>
              <Textarea
                id="descricao"
                placeholder="Descreva os serviços realizados..."
                value={formData.descricao}
                onChange={(e) => setFormData({ ...formData, descricao: e.target.value })}
                rows={4}
                data-testid="form-descricao"
              />
            </div>
            
            <div>
              <Label htmlFor="observacoes">Observações</Label>
              <Textarea
                id="observacoes"
                placeholder="Observações adicionais..."
                value={formData.observacoes}
                onChange={(e) => setFormData({ ...formData, observacoes: e.target.value })}
                rows={2}
                data-testid="form-observacoes"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button 
                type="submit" 
                disabled={submitting}
                className="bg-orange-500 hover:bg-orange-600"
                data-testid="save-maintenance-button"
              >
                {submitting ? 'Salvando...' : (formMode === 'create' ? 'Cadastrar' : 'Salvar')}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir Manutenção</AlertDialogTitle>
            <AlertDialogDescription>
              Tem certeza que deseja excluir esta manutenção de {selectedMaintenance?.vehicle_id ? `${selectedMaintenance.vehicle_id} - ` : ''}{selectedMaintenance?.veiculo_info}?
              Esta ação não pode ser desfeita.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete}
              className="bg-red-600 hover:bg-red-700"
              data-testid="confirm-delete-button"
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
